import {
	type BooleanRuleKey,
	cloneRules,
	type MatchRules,
	MODERN_RULES,
	type NumberRuleKey,
	POWER_UP_KINDS,
	type PowerUpKind,
} from "../sim";
import { el } from "./dom";
import { RULE_TEXT } from "./rule-labels";
import {
	formatOption,
	fromSlider,
	type NumberOption,
	OPTION_SECTIONS,
	type OptionSection,
	POWER_UP_LABELS,
	sliderModel,
	toSlider,
} from "./rule-options";

export interface RulesEditorContext {
	rules: MatchRules;
	/** Longest laser range the chosen map allows (2 × its longest side). */
	maxLaserRange: number;
	/** Every change (already a fresh copy). */
	onChange(rules: MatchRules): void;
	onStart(rules: MatchRules): void;
}

/**
 * The "Custom rules" game options editor: on/off rules as toggle buttons, numbers as sliders
 * (← / → adjust, ↑ / ↓ move between rows via the ScreenManager), per-type power-up switches
 * and a collapsible tuning section. Rows of a switched-off rule are disabled.
 */
export function rulesEditor(ctx: RulesEditorContext): HTMLElement {
	let rules = cloneRules(ctx.rules);
	const syncs: Array<() => void> = [];
	const sync = () => {
		for (const s of syncs) s();
	};
	const commit = () => {
		sync();
		ctx.onChange(cloneRules(rules));
	};

	const toggleRow = (label: string, description: string, read: () => boolean, flip: () => void) => {
		const state = el("span", { class: "toggle-state" });
		const button = el(
			"button",
			{ class: "btn btn-toggle", type: "button" },
			el("span", {}, el("strong", {}, label), el("small", {}, description)),
			state,
		);
		syncs.push(() => {
			button.setAttribute("aria-pressed", String(read()));
			state.textContent = read() ? "On" : "Off";
		});
		button.addEventListener("click", () => {
			flip();
			commit();
		});
		return button;
	};

	const ruleToggle = (key: BooleanRuleKey) =>
		toggleRow(
			RULE_TEXT[key].label,
			RULE_TEXT[key].description,
			() => rules[key],
			() => {
				rules[key] = !rules[key];
			},
		);

	const typeToggle = (kind: PowerUpKind, requires: BooleanRuleKey) => {
		const button = toggleRow(
			POWER_UP_LABELS[kind].label,
			POWER_UP_LABELS[kind].description,
			() => rules.powerUpTypes[kind],
			() => {
				rules.powerUpTypes[kind] = !rules.powerUpTypes[kind];
			},
		);
		button.classList.add("btn-type");
		syncs.push(() => {
			button.disabled = !rules[requires];
		});
		return button;
	};

	const sliderRow = (option: NumberOption, requires: BooleanRuleKey | undefined) => {
		const model = sliderModel(option, ctx.maxLaserRange);
		const id = `opt-${option.key}`;
		const output = el("output", { class: "setting-value", for: id });
		const input = el("input", {
			id,
			type: "range",
			min: model.min,
			max: model.max,
			step: model.step,
			"aria-label": option.label,
		});
		const label = el("label", { for: id }, option.label);
		syncs.push(() => {
			const value = rules[option.key];
			input.value = String(toSlider(option, value, model));
			output.textContent = formatOption(option, value);
			input.setAttribute("aria-valuetext", output.textContent);
			input.disabled = requires !== undefined && !rules[requires];
		});
		input.addEventListener("input", () => {
			setNumber(option.key, fromSlider(option, Number(input.value), model));
			commit();
		});
		return el(
			"div",
			{ class: "setting-row option-row" },
			label,
			input,
			output,
			option.hint ? el("small", { class: "option-hint" }, option.hint) : null,
		);
	};

	/** Writes a number, keeping the power-up interval ordered (min ≤ max). */
	const setNumber = (key: NumberRuleKey, value: number | null) => {
		(rules as unknown as Record<NumberRuleKey, number | null>)[key] = value;
		if (key === "powerUpIntervalMinTicks" && rules.powerUpIntervalMaxTicks < (value ?? 0)) {
			rules.powerUpIntervalMaxTicks = value ?? rules.powerUpIntervalMaxTicks;
		}
		if (key === "powerUpIntervalMaxTicks" && rules.powerUpIntervalMinTicks > (value ?? 0)) {
			rules.powerUpIntervalMinTicks = value ?? rules.powerUpIntervalMinTicks;
		}
	};

	const sectionEl = (section: OptionSection) => {
		const body = el(
			"div",
			{ class: "option-body" },
			...section.toggles.map(ruleToggle),
			...section.numbers.map((n) => sliderRow(n, section.requires)),
		);
		if (section.powerUpTypes && section.requires) {
			const requires = section.requires;
			body.append(
				el(
					"div",
					{ class: "option-types" },
					...POWER_UP_KINDS.map((kind) => typeToggle(kind, requires)),
				),
			);
		}
		const heading = el("h2", {}, section.title);
		if (!section.advanced) {
			return el("section", { class: "settings-section option-section" }, heading, body);
		}
		body.hidden = true;
		const reveal = el(
			"button",
			{ class: "btn btn-small", type: "button", "aria-expanded": "false" },
			"Show tuning",
		);
		reveal.addEventListener("click", () => {
			body.hidden = !body.hidden;
			reveal.setAttribute("aria-expanded", String(!body.hidden));
			reveal.textContent = body.hidden ? "Show tuning" : "Hide tuning";
		});
		return el("section", { class: "settings-section option-section" }, heading, reveal, body);
	};

	const reset = el("button", { class: "btn btn-small", type: "button" }, "Reset to Modern");
	reset.addEventListener("click", () => {
		rules = cloneRules(MODERN_RULES);
		commit();
	});
	const start = el("button", { class: "btn btn-start", type: "button" }, "Start match");
	start.addEventListener("click", () => ctx.onStart(cloneRules(rules)));

	const root = el(
		"div",
		{ class: "settings custom-rules" },
		el("div", { class: "option-actions" }, start, reset),
		...OPTION_SECTIONS.map(sectionEl),
	);
	// Arrow-key navigation moves focus without scrolling; keep the focused row visible.
	root.addEventListener("focusin", (ev) => {
		if (ev.target instanceof HTMLElement) ev.target.scrollIntoView({ block: "nearest" });
	});
	sync();
	return root;
}
